import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

import './SocialButtonsList.scss';
import SocialButtonsList from './SocialButtonsList';
import { useNotNullableContext } from '../../hooks/useNotNullableContext';
import { PageMetaContext } from '../../context/PageMetaContext';
import { filterByTypename } from '../../utils/graphql';
import { useIsMounted } from '../../hooks/useIsMounted';
import useBasePageQuery from '../../hooks/useBasePageQuery';
import { generateEmailBody } from '../../utils/generateEmailBody';

export type SocialButtonsListWrapperProps = {
  message?: string;
  subject?: string;
  suppressGiftAidMessage?: boolean;
};

const SocialButtonsListWrapper: FunctionComponent<SocialButtonsListWrapperProps> = ({
  message,
  subject,
  suppressGiftAidMessage = false,
}) => {
  const result = useBasePageQuery();
  const isMounted = useIsMounted();
  const { pageType, pageShortName, currentUrl } = useNotNullableContext(
    PageMetaContext,
  );
  const { t } = useTranslation('common', { useSuspense: false });

  if (!isMounted || !result.data?.page) {
    return null;
  }

  const { relationships, title } = result.data.page;
  const beneficiaries = relationships?.beneficiaries;
  const charities = filterByTypename(beneficiaries?.nodes ?? [], 'Charity');

  const firstCharityName = charities[0]?.name
    ? charities[0].name
    : t('charity.defaultName');

  const defaultMessage = `${message ||
    t('common.shareMessageGeneric', { charityName: firstCharityName })}`;
  const giftAidMessage = suppressGiftAidMessage
    ? ''
    : t('common.charityGiftAid', {
        charityName: firstCharityName,
        count: (beneficiaries && beneficiaries.totalCount) || 0,
      });

  return (
    <SocialButtonsList
      subject={subject || title || t('common.defaultEmailSubject')}
      messages={{
        default: defaultMessage,
        email: generateEmailBody({
          greeting: t('common.greeting'),
          message: message || defaultMessage,
          currentUrl,
          pageType,
          pageShortName,
          giftAidMessage,
          thanks: t('common.emailThanks'),
        }),
      }}
    />
  );
};

export default SocialButtonsListWrapper;
